import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import content from "../content/roiCalculator.json";
import { RoiCalculatorContent } from "../types/roiCalculator";
import RoiForm from "../components/roi/RoiForm";
import InitialResults from "../components/roi/InitialResults";
import DetailedResults from "../components/roi/DetailedResults";
import { calculateRoi, defaultRoiInput } from "../services/roiCalculator";
import { RoiCalculationInput } from "../types/roiCalculations";
import { Link } from "react-router-dom";
import CostComparisonBarChart from "../components/CostComparisonBarChart";
import MetaTags from "../components/SEO/MetaTags";
import SoftwareApplicationSchema from "../components/SEO/SchemaMarkup/SoftwareApplication";
import { pageConfigs } from "../utils/seo";
import { trackSEOEvents } from "../utils/analytics/ga4";

interface FormValues {
  numOfPts: string;
  sessions: string;
  time: string;
  salary: string;
}

const RoiCalculator = () => {
  const location = useLocation();
  const [formValues, setFormValues] = useState<FormValues>({
    numOfPts: "",
    sessions: "",
    time: "",
    salary: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [calculatedResults, setCalculatedResults] = useState<ReturnType<
    typeof calculateRoi
  > | null>(null);

  // Handle data persistence when navigating back from detailed calculator (but not on page refresh)
  useEffect(() => {
    // Only restore data if explicitly coming from detailed calculator with preserveData flag
    if (location.state?.preserveData && location.state?.quickCalculatorData) {
      const data = location.state.quickCalculatorData;
      if (data.formData) {
        setFormValues({
          numOfPts: data.formData.physicalTherapists || "",
          sessions: data.formData.sessions || "",
          time: data.formData.documentationTime || "",
          salary: data.formData.salary || "",
        });
        
        // Restore calculated results if they exist
        if (data.results) {
          // Recreate the calculation to restore full results
          const calculationInput: RoiCalculationInput = {
            ...defaultRoiInput,
            clinicSize: {
              ...defaultRoiInput.clinicSize,
              small: {
                ...defaultRoiInput.clinicSize.small,
                clinicians: parseInt(data.formData.physicalTherapists) || 1,
              },
            },
            timeMetrics: {
              ...defaultRoiInput.timeMetrics,
              documentationTimePerSession: {
                ...defaultRoiInput.timeMetrics.documentationTimePerSession,
                withoutFownd: {
                  low: parseInt(data.formData.documentationTime) || 0,
                  high: parseInt(data.formData.documentationTime) || 0,
                },
              },
            },
            revenueMetrics: {
              ...defaultRoiInput.revenueMetrics,
              sessionsPerDay: {
                low: parseInt(data.formData.sessions) || 0,
                high: parseInt(data.formData.sessions) || 0,
              },
              ptSalaryPerHour: {
                low: parseInt(data.formData.salary) || 0,
                high: parseInt(data.formData.salary) || 0,
              },
            },
          };
          
          const results = calculateRoi(calculationInput);
          setCalculatedResults(results);
          setIsSubmitted(true);
        }
      }
    }
  }, [location.state]);

  const handleFormChange = (field: keyof FormValues, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields are filled
    if (
      !formValues.numOfPts ||
      !formValues.sessions ||
      !formValues.time ||
      !formValues.salary
    ) {
      return;
    }

    // Track ROI calculator usage
    trackSEOEvents.roiCalculatorStart();

    // Convert form values to calculation input
    const calculationInput: RoiCalculationInput = {
      ...defaultRoiInput,
      clinicSize: {
        ...defaultRoiInput.clinicSize,
        small: {
          ...defaultRoiInput.clinicSize.small,
          clinicians: parseInt(formValues.numOfPts) || 1,
        },
      },
      timeMetrics: {
        ...defaultRoiInput.timeMetrics,
        documentationTimePerSession: {
          ...defaultRoiInput.timeMetrics.documentationTimePerSession,
          withoutFownd: {
            low: parseInt(formValues.time) || 0,
            high: parseInt(formValues.time) || 0,
          },
        },
      },
      revenueMetrics: {
        ...defaultRoiInput.revenueMetrics,
        sessionsPerDay: {
          low: parseInt(formValues.sessions) || 0,
          high: parseInt(formValues.sessions) || 0,
        },
        ptSalaryPerHour: {
          low: parseInt(formValues.salary) || 0,
          high: parseInt(formValues.salary) || 0,
        },
      },
    };

    const results = calculateRoi(calculationInput);
    setCalculatedResults(results);
    setIsSubmitted(true);
    
    // Track ROI calculator completion
    trackSEOEvents.roiCalculatorComplete(results.netProfit.low);
  };

  const handleRefresh = () => {
    setFormValues({
      numOfPts: "",
      sessions: "",
      time: "",
      salary: "",
    });
    setIsSubmitted(false);
    setCalculatedResults(null);
  };

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Subscribe clicked");
  };

  const typedContent = content as RoiCalculatorContent;

  return (
    <>
      <MetaTags
        title={pageConfigs.roiCalculator.title}
        description={pageConfigs.roiCalculator.description}
        keywords={pageConfigs.roiCalculator.keywords}
        canonical={pageConfigs.roiCalculator.canonical}
      />
      <SoftwareApplicationSchema />
      
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Updated Banner with Quick ROI Estimator Title */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1e3d3b] via-[#2a5a56] to-[#1e3d3b]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-[#1e3d3b]/20 to-[#1e3d3b]/40"></div>
        <div className="relative px-6 py-24 md:py-32 lg:py-40">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight font-productSans">
              Unlock Clinics Hidden Profits with{" "}
              <span
                className="text-[#439CCB]"
                style={{ fontFamily: "'Baloo Bhaina 2', sans-serif", fontSize: "1.12em" }}
              >
                notation
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
              Clinics waste hours every week on notes. Notation helps you get that time back — and this quick calculator shows how that translates to real dollars. No complicated forms. Just a simple walkthrough based on your clinic's size and workflow.
            </p>
        </div>
        </div>
        <div className="absolute top-20 left-10 w-20 h-20 bg-white/5 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
      </section>



      <section className="py-20 px-6">
        <div className="mx-auto max-w-7xl">

          <div className="flex flex-col gap-8">
            <div className="flex flex-col md:flex-row items-stretch gap-8 min-h-[600px]">
              <div className="w-full md:w-2/5 flex">
          <RoiForm
            formContent={typedContent.mainContent.form}
            formValues={formValues}
            onFormChange={handleFormChange}
            onSubmit={handleSubmit}
            submitButtonText={typedContent.buttons.formSubmit}
            onRefresh={handleRefresh}
          />
              </div>
              <section className="w-full md:w-3/5 flex">
            {!isSubmitted ? (
              <InitialResults
                results={typedContent.mainContent.results}
                prompt={typedContent.mainContent.prompt}
                onSubmit={handleSubscribe}
                submitButtonText={typedContent.buttons.enquirySubmt}
              />
            ) : (
              calculatedResults && (
                    <div className="bg-white/80 backdrop-blur-sm rounded-lg p-8 shadow-lg h-full flex flex-col lg:flex-row gap-8">
                      <div className="w-full lg:w-1/2">
                        <CostComparisonBarChart
                          withFownd={calculatedResults.totalCost.low}
                          withoutFownd={
                            calculatedResults.totalCost.low +
                            calculatedResults.netProfit.low
                          }
                        />
                      </div>
                      <div className="w-full lg:w-1/2 flex flex-col gap-6">
                        <div className="bg-slate-50 p-6 rounded-lg">
                          <h4 className="text-lg font-semibold text-slate-700 mb-4">
                            Key Metrics
                          </h4>
                          <div className="space-y-4">
                            <div>
                              <div className="text-sm text-slate-500">
                                Cost Savings
                              </div>
                              <div className="text-xl font-bold text-[#619ed3]">
                                $
                                {(
                                  calculatedResults.totalCost.low +
                                  calculatedResults.netProfit.low -
                                  calculatedResults.totalCost.low
                                ).toLocaleString()}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-slate-500">
                                Time Efficiency
                              </div>
                              <div className="text-xl font-bold text-[#619ed3]">
                                {calculatedResults.timeSavings.annualHoursSaved.low.toFixed(
                                  0
                                )}{" "}
                                hours saved
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-slate-50 p-6 rounded-lg">
                          <h4 className="text-lg font-semibold text-slate-700 mb-4">
                            ROI Analysis
                          </h4>
                          <div className="space-y-4">
                            <div>
                              <div className="text-sm text-slate-500">
                                ROI Percentage
                              </div>
                              <div className="text-xl font-bold text-[#619ed3]">
                                {(
                                  (calculatedResults.roi.multiplier.low - 1) *
                                  100
                                ).toFixed(1)}
                                %
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-slate-500">
                                Payback Period
                              </div>
                              <div className="text-xl font-bold text-[#619ed3]">
                                ~6 months
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </section>
            </div>
            {isSubmitted && calculatedResults && (
                <DetailedResults
                  results={{
                    metrics: {
                      revenueGain:
                      calculatedResults.additionalRevenue.fromTimeSavings.low +
                        calculatedResults.additionalRevenue.fromThroughput.low +
                        calculatedResults.additionalRevenue.fromAccuracy.low,
                      hoursSaved:
                      calculatedResults.timeSavings.annualHoursSaved.low,
                    patientRetention: 85,
                    documentationAccuracy: 95,
                    // Add the actual screen cost savings value
                    costSavings: calculatedResults.netProfit.low,
                  },
                  perTherapist: {
                    revenueGain:
                      calculatedResults.perTherapist.revenueGain.low,
                    hoursSaved:
                      calculatedResults.perTherapist.hoursSaved.low,
                    costSavings:
                      calculatedResults.perTherapist.costSavings.low,
                    },
                  }}
                  formData={{
                    physicalTherapists: formValues.numOfPts,
                    sessions: formValues.sessions,
                    documentationTime: formValues.time,
                    salary: formValues.salary,
                  }}
                />
            )}

            {isSubmitted && calculatedResults && (
              <div className="mt-12">
                <div className="relative">
                  <div
                    className="absolute inset-0 flex items-center"
                    aria-hidden="true"
                  >
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-slate-100 px-3 text-lg font-medium text-gray-900">
                      {" "}
                    </span>
                  </div>
                </div>
                <div className="text-center mt-8">
                  <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-700 mb-6 font-productSans">
                    Unlock deeper insights with our Detailed Calculator.
                  </h3>
                  <Link
                    to="/detailed-roi-calculator"
                    state={{
                      quickCalculatorResults: {
                        formData: {
                          physicalTherapists: formValues.numOfPts,
                          sessions: formValues.sessions,
                          documentationTime: formValues.time,
                          salary: formValues.salary,
                        },
                        results: {
                          metrics: {
                            revenueGain:
                              calculatedResults.additionalRevenue.fromTimeSavings.low +
                              calculatedResults.additionalRevenue.fromThroughput.low +
                              calculatedResults.additionalRevenue.fromAccuracy.low,
                            hoursSaved: calculatedResults.timeSavings.annualHoursSaved.low,
                            patientRetention: 85,
                            documentationAccuracy: 95,
                            costSavings: calculatedResults.netProfit.low,
                          },
                          perTherapist: {
                            revenueGain: calculatedResults.perTherapist.revenueGain.low,
                            hoursSaved: calculatedResults.perTherapist.hoursSaved.low,
                            costSavings: calculatedResults.perTherapist.costSavings.low,
                          },
                        },
                      },
                    }}
                    className="inline-block bg-[#1e3d3b] text-white font-semibold px-8 py-4 text-lg rounded-full hover:bg-[#2a5a56] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    Detailed Calculator →
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default RoiCalculator;
